import {
  RadioButton,
  InputField,
  Button,
  Spinner,
  ErrorMessage,
  Heading,
  AgGrid,
  SuccessPopup,
  CommonLink,
  SortDropdownPagination,
} from "@/common/components";
import { useState, useEffect, useContext } from "@/common/hooks";
import { PrivateContext } from "@/routes/PrivateRoute";
import {
  useGetAllAccessProfilesForProfileQuery,
  useLazyGetAccessorTransactionInfoQuery,
} from "@/service/ProfileService";
import {
  useSearchOperatorForAdminMutation,
  useGetAccessRuleAuditHistoryForAdminMutation,
  useUpdateOperatorFromAdminMutation,
} from "@/service/AdminService";
import { useGetProductManufacturersQuery } from "@/service/APIService";
import ViewContactInformation from "@/components/Common/ViewContactInformation";
import ProfileInformation from "@/components/Common/ProfileInformation";
import {
  getStatus,
  getFormattedDateTime,
  getFormattedDate,
  scrollToTop,
} from "@/utils/Utilities";
import unionBy from "lodash/unionBy";
import dayjs from "dayjs";

const UpdateOperatorInformation = () => {
  const {
    debounceState,
    setNavHeader,
    cloneDeep,
    cancel,
    sortBy,
    labels,
    setPageHeader,
  } = useContext(PrivateContext);

  const searchByData = {
    header: labels?.updateOperatorInformation?.searchBy,
    data: [
      {
        name: "Last Name",
        label: labels?.updateOperatorInformation?.lastName,
        id: "L",
      },
      {
        name: "Operator ID",
        label: labels?.updateOperatorInformation?.operatorId,
        id: "O",
      },
    ],
  };

  const statusData = {
    data: [
      {
        name: "Active",
        label: labels?.updateOperatorInformation?.active,
        id: "E",
      },
      {
        name: "Suspended",
        label: labels?.updateOperatorInformation?.suspended,
        id: "S",
      },
      {
        name: "Terminated",
        label: labels?.updateOperatorInformation?.terminated,
        id: "T",
      },
    ],
  };

  const columnDefs = [
    {
      headerName: labels?.updateOperatorInformation?.date,
      field: "lastMntDateTime",
      flex: 2,
      cellRenderer: getFormattedDateTime,
    },
    {
      field: "firstName",
    },
    {
      field: "lastName",
    },
    {
      headerName: labels?.updateOperatorInformation?.productManufacturer,
      field: "productSponsorName",
      flex: 2,
    },
    {
      field: "newAccessLevel",
      flex: 1.5,
    },
    {
      field: "status",
      cellRenderer: getStatus,
      flex: 1.5,
    },
    {
      headerName: labels?.updateOperatorInformation?.operatorId,
      field: "lastMntOpId",
    },
  ];

  const columnDefsForOperator = [
    {
      headerName: labels?.updateOperatorInformation?.firstName,
      field: "contact.firstName",
    },
    {
      headerName: labels?.updateOperatorInformation?.lastName,
      field: "contact.lastName",
    },
  ];

  const [selectedSearch, setSelectedSearch] = useState("L");
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState(false);
  const [accessorsData, setAccessorsData] = useState([]);
  const [contactInfo, setContactInfo] = useState();
  const [dataAccessRules, setDataAccessRules] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState("");
  const [selectedAccessorId, setSelectedAccessorId] = useState("");
  const [currentAccessProfile, setCurrentAccessProfile] = useState({});
  const [selectedStatus, setSelectedStatus] = useState("");
  const [adminChecked, setAdminChecked] = useState(false);
  const [auditData, setAuditData] = useState();
  const [rowData, setRowData] = useState([]);
  const [insuranceCompanyId, setInsuranceCompanyId] = useState(undefined);
  const [showPopup, setShowPopup] = useState(false);
  const [showPopup2, setShowPopup2] = useState(false);
  const [showHistoryGrid, setShowHistoryGrid] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [showEnableBtn, setShowEnableBtn] = useState(true);
  const itemsPerPage = 3;
  const startIndex = currentPage * itemsPerPage;

  const [searchOperatorApi, { isLoading: isFetching, error }] =
    useSearchOperatorForAdminMutation();

  const {
    data: accessProfiles,
    isFetching: isFetching1,
    error: error1,
  } = useGetAllAccessProfilesForProfileQuery();

  const [
    accessorTransactionInfoApi,
    { isFetching: isFetching2, error: error2 },
  ] = useLazyGetAccessorTransactionInfoQuery();

  const [auditRulesHistoryApi, { isFetching: isFetching3, error: error3 }] =
    useGetAccessRuleAuditHistoryForAdminMutation();

  const { data: allProductManufacturers, isFetching: isFetching5 } =
    useGetProductManufacturersQuery({
      insuranceCompanyId: insuranceCompanyId,
    });

  const [updateOperatorApi, { isFetching: isFetching4, error: error4 }] =
    useUpdateOperatorFromAdminMutation();

  const resetStates = () => {
    setContactInfo({});
    setCurrentAccessProfile({});
    setSelectedStatus("");
    setAuditData(null);
    setRowData([]);
    setShowHistoryGrid(false);
  };

  // Search using lastName or Operator ID
  const doSearch = () => {
    setAccessorsData([]);
    resetStates();
    setSearch(selectedSearch);

    searchOperatorApi({
      lastName: selectedSearch === "L" ? searchText : "",
      operatorId: selectedSearch === "O" ? searchText : "",
    })
      .unwrap()
      .then((res) => {
        if (res.accessors) {
          const sortedArray = cloneDeep(res.accessors).sort((a, b) => {
            return a.contact.firstName.localeCompare(b.contact.firstName);
          });
          setAccessorsData(sortedArray);
          // If only 1 accessor is returned, auto fetch its data.
          if (res.accessors.length === 1) {
            doOperatorSearch(res.accessors[0].operatorId);
          }
        }
      });
  };

  const filterDuplicateAuditRow = (item, dataProvider) => {
    const currentRow = dataProvider.indexOf(item);
    if (currentRow < dataProvider.length - 1) {
      const nextItem = dataProvider[currentRow + 1];
      const isEqual =
        item.firstName === nextItem.firstName &&
        item.lastName === nextItem.lastName &&
        item.productSponsorName === nextItem.productSponsorName &&
        item.newAccessLevel === nextItem.newAccessLevel &&
        item.status === nextItem.status &&
        item.auditJournalEntryTypeCode === nextItem.auditJournalEntryTypeCode;

      return !isEqual;
    }

    return true;
  };

  // Fetch the accessor data rules, audit history, status
  const doOperatorSearch = (id) => {
    resetStates();
    setSelectedAccessorId(id);
    setInsuranceCompanyId(undefined);

    accessorTransactionInfoApi(id)
      .unwrap()
      .then((res) => {
        setContactInfo(res?.contact);
        setCurrentAccessProfile(res);
        setSelectedStatus(res?.status === "D" ? "E" : res?.status);
      });

    auditRulesHistoryApi({
      accessorId: id,
      firstResult: 0,
      maxResults: 100,
    })
      .unwrap()
      .then((res) => {
        setAuditData(res?.accessorDataAccessRuleAudits);
        setRowData(
          res?.accessorDataAccessRuleAudits.filter((item) =>
            filterDuplicateAuditRow(item, res?.accessorDataAccessRuleAudits)
          )
        );
      });
  };

  // Enable Operator by passing new status as E.
  const enableOperator = () => {
    const payload = {
      operatorId: currentAccessProfile.operatorId,
      requestOfRiccAdmin: selectedEntity === "R" ? adminChecked : "false",
      newStatus: "E",
      lastMntOptId: currentAccessProfile.lastMntOpId,
      lastMntDateTime: currentAccessProfile.lastMntDateTime,
      dataAccessRules: null,
    };
    updateOperatorApi(payload)
      .unwrap()
      .then((res) => {
        if (!res?.inError) {
          setShowPopup2(true);
          setShowEnableBtn(false);
          doOperatorSearch(currentAccessProfile.operatorId);
        }
      });
  };

  const updateOperator = () => {
    const rules = cloneDeep(dataAccessRules);
    for (let i = 0; i < rules.length; i++) {
      if (rules[i]?.requestAccessProfile?.id) {
        rules[i].requestStatus = "A";
      } else if (rules[i]?.approveAccessProfile?.id) {
        rules[i].requestStatus = "A";
        rules[i].requestAccessProfile.id = rules[i].approveAccessProfile.id;
      }
    }

    const payload = {
      operatorId: currentAccessProfile.operatorId,
      requestOfRiccAdmin: selectedEntity === "R" ? adminChecked : "false",
      newStatus: selectedStatus,
      lastMntOptId: currentAccessProfile.lastMntOpId,
      lastMntDateTime: currentAccessProfile.lastMntDateTime,
      dataAccessRules: rules,
    };

    updateOperatorApi(payload)
      .unwrap()
      .then((res) => {
        scrollToTop();
        if (!res?.inError) {
          setShowPopup(true);
        }
      });
  };

  const doContinue = () => {
    setShowPopup(false);
    setAccessorsData([]);
    resetStates();
    setSearchText("");
    setSearch(false);
    setSelectedAccessorId("");
  };

  const closePopUp = () => {
    setShowPopup2(false);
  };

  const onGridReady = (e) => {
    e.api.sizeColumnsToFit();
  };

  const getRowClass = (params) => {
    if (params.data.operatorId === selectedAccessorId) {
      return "hover-bg";
    }
    return "";
  };

  const onRowClicked = (e) => {
    setSelectedAccessorId(e.data.operatorId);
    doOperatorSearch(e.data.operatorId);
  };

  useEffect(() => {
    if (currentAccessProfile && currentAccessProfile.status) {
      let entity = "";
      let productSponsors = cloneDeep(
        allProductManufacturers?.productSponsors
      ).map((e) => {
        return {
          productSponsor: e,
          requestAccessProfile: { id: "" },
        };
      });

      // Insurance Company
      // Fetch product sponsors based on insurance company ID
      if (currentAccessProfile?.insuranceCompany) {
        entity = "I";
        setInsuranceCompanyId(currentAccessProfile?.insuranceCompany?.id);
      }
      // Product Sponsor
      else if (currentAccessProfile?.productSponsor) {
        entity = "P";
        productSponsors = productSponsors.filter(
          (e) =>
            e?.productSponsor?.id === currentAccessProfile?.productSponsor?.id
        );
      }
      // RICC Administrator
      else {
        entity = "R";
        setAdminChecked(currentAccessProfile?.accessProfile ? true : false);
      }
      setSelectedEntity(entity);

      // When there is no accessProfile and dataAccessRuleList OR when there is only accessProfile
      if (
        (!currentAccessProfile?.accessProfile &&
          !currentAccessProfile?.dataAccessRuleList) ||
        currentAccessProfile?.accessProfile
      ) {
        setDataAccessRules(productSponsors);
        return;
      }

      // When there is dataAccessRuleList
      let accessRules = cloneDeep(currentAccessProfile?.dataAccessRuleList).map(
        (e) => {
          return {
            ...e,
            requestAccessProfile: {
              id: e?.requestAccessProfile?.id
                ? e?.requestAccessProfile?.id
                : "",
            },
          };
        }
      );

      // For those profiles who do not any approved/requested access levels. (Only for RICC Administrator)
      if (entity === "R" || entity === "I") {
        accessRules = sortBy(
          unionBy(accessRules, productSponsors, "productSponsor.id"),
          "productSponsor.name"
        );
      }
      setDataAccessRules(accessRules);
    }
  }, [currentAccessProfile, allProductManufacturers]);

  useEffect(() => {
    setPageHeader(labels?.updateOperatorInformation?.title);
    setNavHeader(labels?.navbar?.admin);
  }, []);

  return (
    <section className="container-x-large py-5">
      {(isFetching ||
        isFetching1 ||
        isFetching2 ||
        isFetching3 ||
        isFetching4 ||
        isFetching5) && <Spinner />}
      <ErrorMessage
        error={error || error1 || error2 || error3 || error4}
        wrapperClassName="mb-2"
      />
      <RadioButton
        wrapperClassName="mb-3"
        data={searchByData}
        value={selectedSearch}
        selected={(e) => setSelectedSearch(e)}
      />

      <InputField
        id="searchText"
        wrapperClassName="col-md-3"
        value={searchText}
        change={(e) => {
          debounceState(e, setSearchText);
        }}
        ariaLabel="Search"
      />

      <Button
        id="searchButton"
        text={labels?.updateOperatorInformation?.search}
        wrapperClassName="col-md-2 mb-4"
        click={doSearch}
        disabled={selectedSearch === "L" && !searchText}
      />

      {search && !isFetching && (
        <>
          <AgGrid
            wrapperClassNames="mb-4"
            columnDefs={columnDefsForOperator}
            rowData={accessorsData}
            domLayout="normal"
            getRowClass={getRowClass}
            onRowClicked={onRowClicked}
            overlayNoRowsTemplate={`${
              labels?.updateOperatorInformation?.noMatchesFound
            } ${
              search === "L"
                ? labels?.updateOperatorInformation?.lastNameSmall
                : labels?.updateOperatorInformation?.operatorIdSmall
            }`}
          />

          {contactInfo && contactInfo.firstName && (
            <>
              {/* Enable Operator */}
              {currentAccessProfile &&
                currentAccessProfile.status === "D" &&
                selectedStatus === "E" && (
                  <>
                    {showEnableBtn && (
                      <>
                        <div className="mb-2">
                          {labels?.updateOperatorInformation?.threeAttempts}
                        </div>

                        <Button
                          id="enable"
                          text={labels?.updateOperatorInformation?.enable}
                          wrapperClassName="col-md-2 mb-4"
                          click={enableOperator}
                        />
                      </>
                    )}
                  </>
                )}

              {/* Contact Information and Status Information */}

              <div className="mb-4">
                <ViewContactInformation
                  contactInfo={contactInfo}
                  key={contactInfo}
                  labels={labels.updateOperatorInformation}
                  wrapperClassName="row mx-0"
                  section1ClassName="col-md-4 ps-0 pe-0 pe-md-3"
                  section2ClassName="col-md-4 ps-0 ps-md-3 pe-0 pe-md-3"
                  section3ClassName="col-md-4 pe-0 ps-0 ps-md-3"
                />
              </div>

              <div className="mb-4">
                <Heading
                  text={labels?.updateOperatorInformation?.statusInfo}
                  wrapperClassNames="h4 border-bottom-1 pb-2"
                  title={false}
                />
                <div>
                  {labels?.updateOperatorInformation?.currentStatusEstablished}
                  {getFormattedDate(currentAccessProfile?.statusDate, true)}
                </div>
                <div>
                  {labels?.updateOperatorInformation?.status}
                  {["P", "R"].indexOf(selectedStatus) > -1 ? (
                    getStatus(selectedStatus)
                  ) : (
                    <RadioButton
                      wrapperClassName="mb-3"
                      data={statusData}
                      value={selectedStatus}
                      selected={(e) => setSelectedStatus(e)}
                    />
                  )}
                </div>
              </div>
            </>
          )}

          {/* Access Rules Table */}
          {currentAccessProfile &&
            currentAccessProfile.operatorId &&
            ["P", "R"].indexOf(selectedStatus) < 0 && (
              <section className="mb-4">
                <ProfileInformation
                  accessProfiles={accessProfiles}
                  currentAccessProfile={currentAccessProfile}
                  selectedEntity={selectedEntity}
                  dataAccessRules={dataAccessRules}
                  setDataAccessRules={setDataAccessRules}
                  addNoAccess={true}
                  showOperatorId={false}
                  showCheckbox={selectedEntity === "R"}
                  adminChecked={adminChecked}
                  setAdminChecked={setAdminChecked}
                  labels={labels?.updateOperatorInformation}
                />
                <div className="d-flex gap-1 flex-row justify-content-end">
                  <Button
                    id="cancel"
                    text={labels?.updateOperatorInformation?.cancel}
                    type="secondary"
                    wrapperClassName="col-md-2 col-6"
                    click={cancel}
                  />

                  <Button
                    id="Done"
                    text={labels?.updateOperatorInformation?.done}
                    wrapperClassName="col-md-2 col-6"
                    click={updateOperator}
                  />
                </div>
              </section>
            )}

          {/* Ag Grid to display Audit History */}
          {auditData && (
            <>
              <Heading
                text={labels?.updateOperatorInformation?.operatorInfo}
                wrapperClassNames="h4 border-bottom-1 pb-2"
                title={false}
              />

              <div className="grid-detail-box py-4 mb-4 d-flex justify-content-center">
                <CommonLink
                  text={`${
                    !showHistoryGrid
                      ? labels?.updateOperatorInformation?.show
                      : labels?.updateOperatorInformation?.hide
                  } ${labels?.updateOperatorInformation?.operatorHistory}`}
                  click={() => setShowHistoryGrid(!showHistoryGrid)}
                />
              </div>

              {showHistoryGrid && (
                <>
                  <AgGrid
                    columnDefs={columnDefs}
                    rowData={rowData}
                    onGridReady={onGridReady}
                    domLayout="normal"
                    overlayNoRowsTemplate={
                      labels?.updateOperatorInformation?.noHistory
                    }
                    wrapperClassNames="d-none d-md-block"
                  />
                  <div className="only-ipad">
                    <SortDropdownPagination
                      allEntities={rowData}
                      currentPage={currentPage}
                      setCurrentPage={setCurrentPage}
                      showSortDropDown={false}
                      itemsPerPage={itemsPerPage}
                      labels={labels}
                    />
                    {rowData.length > 0 ? (
                      rowData
                        .slice(startIndex, startIndex + itemsPerPage)
                        .map((entity, index) => (
                          <table
                            key={index}
                            className="table mb-0 border-0 border-bottom"
                          >
                            <tbody>
                              <tr className="align-middle">
                                <th className="py-1 border-0" scope="row">
                                  {labels?.updateOperatorInformation?.date}
                                </th>
                                <td className="py-1 text-end border-0">
                                  {dayjs(entity?.lastMntDateTime).format(
                                    "MM/DD/YYYY h:mm:ss A"
                                  )}
                                </td>
                              </tr>
                              <tr className="align-middle">
                                <th className="py-1 border-0" scope="row">
                                  {labels?.updateOperatorInformation?.firstName}
                                </th>
                                <td className="py-1 text-end border-0">
                                  {entity?.firstName}
                                </td>
                              </tr>
                              <tr className="align-middle">
                                <th className="py-1 border-0" scope="row">
                                  {labels?.updateOperatorInformation?.lastName}
                                </th>
                                <td className="py-1 text-end border-0">
                                  {entity?.lastName}
                                </td>
                              </tr>
                              <tr className="align-middle">
                                <th className="py-1 border-0" scope="row">
                                  {
                                    labels?.updateOperatorInformation
                                      ?.productManufacturer
                                  }
                                </th>
                                <td className="py-1 text-end border-0">
                                  {entity?.productSponsorName}
                                </td>
                              </tr>
                              <tr className="align-middle">
                                <th className="py-1 border-0" scope="row">
                                  {
                                    labels?.updateOperatorInformation
                                      ?.newAccessLevel
                                  }
                                </th>
                                <td className="py-1 text-end border-0">
                                  {entity?.newAccessLevel}
                                </td>
                              </tr>
                              <tr className="align-middle">
                                <th className="py-1 border-0" scope="row">
                                  {labels?.updateOperatorInformation?.status}
                                </th>
                                <td className="py-1 text-end border-0">
                                  {getStatus(entity?.status)}
                                </td>
                              </tr>
                              <tr className="align-middle">
                                <th className="py-1 border-0" scope="row">
                                  {
                                    labels?.updateOperatorInformation
                                      ?.operatorId
                                  }
                                </th>
                                <td className="py-1 text-end border-0">
                                  {entity?.lastMntOpId}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        ))
                    ) : (
                      <div className="d-flex align-items-center justify-content-center border-bottom py-4">
                        <p>{labels?.updateOperatorInformation?.noHistory}</p>
                      </div>
                    )}
                    <SortDropdownPagination
                      allEntities={rowData}
                      currentPage={currentPage}
                      setCurrentPage={setCurrentPage}
                      showSortDropDown={false}
                      itemsPerPage={itemsPerPage}
                      labels={labels}
                    />
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}

      <SuccessPopup
        showModal={showPopup}
        buttonText={labels?.updateOperatorInformation?.continue}
        click={doContinue}
        body={labels?.updateOperatorInformation?.successMsg}
      />

      <SuccessPopup
        showModal={showPopup2}
        buttonText={labels?.updateOperatorInformation?.close}
        click={closePopUp}
        body={labels?.updateOperatorInformation?.successMsg}
      />
    </section>
  );
};

export default UpdateOperatorInformation;
